const DMS_API_URL = process.env.NEXT_PUBLIC_DMS_API_URL || 'api-dms.alfarishost.com';

/** In browser use same origin so Next.js rewrites proxy to DMS API (avoids CORS "Failed to fetch"). */
function getBaseUrl(): string {
  if (typeof window !== 'undefined') return window.location.origin;
  return DMS_API_URL;
}

type FetchOptions = RequestInit & {
  params?: Record<string, string>;
};

export async function apiFetch<T>(endpoint: string, options: FetchOptions = {}): Promise<T> {
  const { params, ...init } = options;
  const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  const url = new URL(path, getBaseUrl());

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      url.searchParams.append(key, value);
    });
  }

  const token = typeof window !== 'undefined' ? localStorage.getItem('dms_token') : null;
  const headers = new Headers(init.headers);
  
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  
  if (!headers.has('Content-Type') && !(init.body instanceof FormData) && !(init.body instanceof Blob)) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(url.toString(), {
    ...init,
    headers,
  });

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { error: response.statusText };
    }
    
    // Auto logout on 401
    if (response.status === 401 && typeof window !== 'undefined') {
      localStorage.removeItem('dms_token');
      localStorage.removeItem('dms_user');
      window.location.href = '/login';
    }

    const err =
      errorData && typeof errorData === 'object'
        ? { ...(errorData as Record<string, unknown>), status: response.status }
        : { error: String(errorData), status: response.status };
    throw err;
  }

  if (response.status === 204) {
    return {} as T;
  }

  return response.json();
}

export const dmsApi = {
  auth: {
    login: (body: any) => apiFetch<any>('/api/auth/login', { method: 'POST', body: JSON.stringify(body) }),
    me: () => apiFetch<any>('/api/auth/me'),
  },
  departments: {
    list: () => apiFetch<any[]>('/api/departments'),
    get: (id: string) => apiFetch<any>(`/api/departments/${id}`),
    getRootFolder: (id: string) => apiFetch<any>(`/api/departments/${id}/root-folder`),
    create: (body: { name: string }) =>
      apiFetch<any>('/api/departments', { method: 'POST', body: JSON.stringify(body) }),
  },
  folders: {
    get: (id: string) => apiFetch<any>(`/api/folders/${id}`),
    getChildren: (id: string, includeDeleted = false) => 
      apiFetch<any>(`/api/folders/${id}/children`, { params: { includeDeleted: String(includeDeleted) } }),
    create: (body: any) => apiFetch<any>('/api/folders', { method: 'POST', body: JSON.stringify(body) }),
    rename: (id: string, name: string) => apiFetch<any>(`/api/folders/${id}/rename`, { method: 'PATCH', body: JSON.stringify({ name }) }),
    delete: (id: string) => apiFetch<void>(`/api/folders/${id}`, { method: 'DELETE' }),
    restore: (id: string) => apiFetch<any>(`/api/folders/${id}/restore`, { method: 'POST' }),
  },
  files: {
    get: (id: string) => apiFetch<any>(`/api/files/${id}`),
    requestUpload: (body: { folderId: string; originalName: string; mimeType?: string; size?: number }) => 
      apiFetch<{ fileId: string; presignedUrl: string; storageKey: string; expiresIn: number }>('/api/files/request-upload', { 
        method: 'POST', 
        body: JSON.stringify(body) 
      }),
    uploadBinary: (token: string, file: Blob) => 
      apiFetch<void>('/api/files/upload', { 
        method: 'PUT', 
        headers: { 
          'X-Upload-Token': token,
          'Content-Type': file.type || 'application/octet-stream'
        }, 
        body: file 
      }),
    confirmUpload: (id: string) => apiFetch<any>(`/api/files/${id}/confirm-upload`, { method: 'POST' }),
    delete: (id: string) => apiFetch<void>(`/api/files/${id}`, { method: 'DELETE' }),
    restore: (id: string) => apiFetch<any>(`/api/files/${id}/restore`, { method: 'POST' }),
    /** PATCH /api/files/:fileId/move — body: { newFolderId }. File and folder must be in same department. */
    move: (fileId: string, newFolderId: string) =>
      apiFetch<any>(`/api/files/${fileId}/move`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newFolderId }),
      }),
    rename: (id: string, originalName: string) => apiFetch<any>(`/api/files/${id}/rename`, { method: 'PATCH', body: JSON.stringify({ originalName }) }),
    search: (q: string, limit = 50, options?: { departmentId?: string; createdFrom?: string; createdTo?: string }) =>
      apiFetch<{ files: any[]; folders: any[] }>('/api/files/search', {
        params: {
          ...(q ? { q } : {}),
          limit: String(limit),
          ...(options?.departmentId ? { departmentId: options.departmentId } : {}),
          ...(options?.createdFrom ? { createdFrom: options.createdFrom } : {}),
          ...(options?.createdTo ? { createdTo: options.createdTo } : {}),
        },
      }),
    getFileViewUrl: (id: string) => {
      const token = typeof window !== 'undefined' ? localStorage.getItem('dms_token') : null;
      const base = typeof window !== 'undefined' ? window.location.origin : DMS_API_URL;
      return `${base}/api/files/${id}/view?token=${token}`;
    },
    getFileDownloadUrl: (id: string) => {
      const token = typeof window !== 'undefined' ? localStorage.getItem('dms_token') : null;
      const base = typeof window !== 'undefined' ? window.location.origin : DMS_API_URL;
      return `${base}/api/files/${id}/download?token=${token}`;
    },
    /** Download file using Bearer token (same auth as other API calls). Triggers browser download. */
    async download(fileId: string, filename?: string | null): Promise<void> {
      const base = typeof window !== 'undefined' ? window.location.origin : DMS_API_URL;
      const url = `${base}/api/files/${fileId}/download`;
      const token = typeof window !== 'undefined' ? localStorage.getItem('dms_token') : null;
      const headers: HeadersInit = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(url, { headers });
      if (!res.ok) {
        let message = res.statusText || 'Download failed';
        try {
          const text = await res.text();
          if (text) {
            const body = JSON.parse(text);
            if (body && typeof body.error === 'string') message = body.error;
            else message = text;
          }
        } catch {
          /* use message as-is */
        }
        throw new Error(message);
      }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const safeName = (filename && filename.trim()) || 'download';
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = safeName;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    },
  },
  trash: {
    list: () => apiFetch<{ items: any[] }>('/api/trash'),
    restore: (id: string, type: 'folder' | 'file') =>
      apiFetch<any>('/api/trash/restore', { method: 'POST', body: JSON.stringify({ id, type }) }),
  },
  bulk: {
    delete: (body: { folderIds?: string[]; fileIds?: string[] }) =>
      apiFetch<{ deleted: { folders: string[]; files: string[] }; errors: Array<{ type: string; id: string; error: string }> }>(
        '/api/bulk/delete',
        { method: 'POST', body: JSON.stringify(body) }
      ),
    copy: (body: { folderIds?: string[]; fileIds?: string[]; destinationFolderId: string }) =>
      apiFetch<{ copied: { folders: string[]; files: string[] }; errors: Array<{ type: string; id: string; error: string }> }>(
        '/api/bulk/copy',
        { method: 'POST', body: JSON.stringify(body) }
      ),
  },
  licenses: {
    dashboard: (params?: Record<string, string>) =>
      apiFetch<any>('/api/licenses/dashboard', { params }),
    list: (params?: Record<string, string>) =>
      apiFetch<{ items: any[]; total: number }>('/api/licenses', { params }),
    folders: (params?: Record<string, string>) =>
      apiFetch<{ root: any; folders: any[] }>('/api/licenses/folders', { params }),
    createFolder: (body: { name: string; parentId: string }, params?: Record<string, string>) =>
      apiFetch<any>('/api/licenses/folders', { method: 'POST', body: JSON.stringify(body), params }),
    get: (id: string, params?: Record<string, string>) =>
      apiFetch<any>(`/api/licenses/${id}`, { params }),
    upload: async (file: File, options?: { renewalOfId?: string; departmentId?: string; folderId?: string }) => {
      const form = new FormData();
      form.append('file', file);
      if (options?.renewalOfId) form.append('renewalOfId', options.renewalOfId);
      if (options?.departmentId) form.append('departmentId', options.departmentId);
      if (options?.folderId) form.append('folderId', options.folderId);
      const token = typeof window !== 'undefined' ? localStorage.getItem('dms_token') : null;
      const url = new URL('/api/licenses/upload', getBaseUrl());
      if (options?.departmentId) {
        url.searchParams.set('departmentId', options.departmentId);
      }
      if (options?.folderId) {
        url.searchParams.set('folderId', options.folderId);
      }
      const res = await fetch(url.toString(), {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        throw { ...err, status: res.status };
      }
      return res.json();
    },
    confirmMetadata: (id: string, body: any, params?: Record<string, string>) =>
      apiFetch<any>(`/api/licenses/${id}/metadata`, { method: 'PATCH', body: JSON.stringify(body), params }),
    retryScan: (id: string, params?: Record<string, string>) =>
      apiFetch<any>(`/api/licenses/${id}/scan/retry`, { method: 'POST', params }),
    audit: (id: string, params?: Record<string, string>) =>
      apiFetch<{ items: any[] }>(`/api/licenses/${id}/audit`, { params }),
    versions: (id: string, params?: Record<string, string>) =>
      apiFetch<{ items: any[] }>(`/api/licenses/${id}/versions`, { params }),
    report: (type: string, params?: Record<string, string>) =>
      apiFetch<{ items: any[] }>(`/api/licenses/reports/${type}`, { params }),
    notificationConfig: (params?: Record<string, string>) =>
      apiFetch<any>('/api/licenses/notification-config', { params }),
    updateNotificationConfig: (body: any, params?: Record<string, string>) =>
      apiFetch<any>('/api/licenses/notification-config', { method: 'PUT', body: JSON.stringify(body), params }),
    notificationHistory: (params?: Record<string, string>) =>
      apiFetch<{ items: any[] }>('/api/licenses/notifications/history', { params }),
    notificationInbox: (params?: Record<string, string>) =>
      apiFetch<{ items: any[] }>('/api/licenses/notifications/inbox', { params }),
    archive: (id: string, params?: Record<string, string>) =>
      apiFetch<any>(`/api/licenses/${id}/archive`, { method: 'POST', params }),
    delete: (id: string, params?: Record<string, string>) =>
      apiFetch<{ deleted: boolean; id: string }>(`/api/licenses/${id}`, { method: 'DELETE', params }),
    getFileViewUrl: (dmsFileId: string) => {
      const token = typeof window !== 'undefined' ? localStorage.getItem('dms_token') : null;
      const base = typeof window !== 'undefined' ? window.location.origin : DMS_API_URL;
      return `${base}/api/files/${dmsFileId}/view?token=${token}`;
    },
  },
  correspondence: {
    dashboard: (params?: Record<string, string>) =>
      apiFetch<any>('/api/correspondence/dashboard', { params }),
    list: (params?: Record<string, string>) =>
      apiFetch<{ items: any[]; total: number }>('/api/correspondence', { params }),
    get: (id: string, params?: Record<string, string>) =>
      apiFetch<any>(`/api/correspondence/${id}`, { params }),
    /** Resolve a scanned QR code to its transaction and department. */
    resolveQr: (id: string, token: string) =>
      apiFetch<{ departmentId: string; transaction: any }>(`/api/correspondence/resolve/${id}`, {
        params: { t: token },
      }),
    qr: (id: string, params?: Record<string, string>) =>
      apiFetch<{ transactionNumber: string; title: string; targetUrl: string; dataUrl: string | null }>(
        `/api/correspondence/${id}/qr`,
        { params }
      ),
    documentPdfInfo: (id: string, params?: Record<string, string>) =>
      apiFetch<{
        hasDocument: boolean;
        isPdf: boolean;
        pageCount: number;
        pages?: Array<{ pageNumber: number; width: number; height: number }>;
        originalName?: string;
        mimeType?: string;
        dmsFileId?: string;
      }>(`/api/correspondence/${id}/document-pdf-info`, { params }),
    applyQrToPdf: (
      id: string,
      body: {
        placement: 'new_page' | 'existing_page' | 'custom';
        pageNumber?: number;
        addNewPage?: boolean;
        x?: number;
        y?: number;
        qrWidth?: number;
      },
      params?: Record<string, string>
    ) =>
      apiFetch<{
        transactionNumber: string;
        title: string;
        targetUrl: string;
        dataUrl: string | null;
        placement: string;
        pageNumber: number | null;
        dmsFileId: string;
        applied: boolean;
      }>(`/api/correspondence/${id}/qr/apply-to-pdf`, {
        method: 'POST',
        body: JSON.stringify(body),
        params,
      }),
    history: (id: string, params?: Record<string, string>) =>
      apiFetch<{ items: any[] }>(`/api/correspondence/${id}/history`, { params }),
    retryScan: (id: string, params?: Record<string, string>) =>
      apiFetch<any>(`/api/correspondence/${id}/scan/retry`, { method: 'POST', params }),
    update: (id: string, body: any, params?: Record<string, string>) =>
      apiFetch<any>(`/api/correspondence/${id}`, { method: 'PATCH', body: JSON.stringify(body), params }),
    delete: (id: string, params?: Record<string, string>) =>
      apiFetch<{ deleted: boolean; id: string }>(`/api/correspondence/${id}`, { method: 'DELETE', params }),
    /** Multipart create: main document + optional attachments. */
    create: async (
      fields: Record<string, string>,
      files: { document?: File | null; attachments?: File[] },
      options?: { departmentId?: string }
    ) => {
      const form = new FormData();
      Object.entries(fields).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') form.append(key, value);
      });
      if (files.document) form.append('document', files.document);
      (files.attachments || []).forEach((f) => form.append('attachments', f));
      if (options?.departmentId) form.append('departmentId', options.departmentId);

      const token = typeof window !== 'undefined' ? localStorage.getItem('dms_token') : null;
      const url = new URL('/api/correspondence', getBaseUrl());
      if (options?.departmentId) url.searchParams.set('departmentId', options.departmentId);

      const res = await fetch(url.toString(), {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        throw { ...err, status: res.status };
      }
      return res.json();
    },
    addAttachments: async (id: string, attachments: File[], options?: { departmentId?: string }) => {
      const form = new FormData();
      attachments.forEach((f) => form.append('attachments', f));
      if (options?.departmentId) form.append('departmentId', options.departmentId);

      const token = typeof window !== 'undefined' ? localStorage.getItem('dms_token') : null;
      const url = new URL(`/api/correspondence/${id}/attachments`, getBaseUrl());
      if (options?.departmentId) url.searchParams.set('departmentId', options.departmentId);

      const res = await fetch(url.toString(), {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        throw { ...err, status: res.status };
      }
      return res.json();
    },
    setDocument: async (id: string, document: File, options?: { departmentId?: string }) => {
      const form = new FormData();
      form.append('document', document);
      if (options?.departmentId) form.append('departmentId', options.departmentId);

      const token = typeof window !== 'undefined' ? localStorage.getItem('dms_token') : null;
      const url = new URL(`/api/correspondence/${id}/document`, getBaseUrl());
      if (options?.departmentId) url.searchParams.set('departmentId', options.departmentId);

      const res = await fetch(url.toString(), {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        throw { ...err, status: res.status };
      }
      return res.json();
    },
    getFileViewUrl: (dmsFileId: string) => {
      const token = typeof window !== 'undefined' ? localStorage.getItem('dms_token') : null;
      const base = typeof window !== 'undefined' ? window.location.origin : DMS_API_URL;
      return `${base}/api/files/${dmsFileId}/view?token=${token}`;
    },
  },
  roles: {
    list: () => apiFetch<any[]>('/api/roles'),
    permissions: () => apiFetch<any[]>('/api/roles/permissions'),
    create: (body: any) => apiFetch<any>('/api/roles', { method: 'POST', body: JSON.stringify(body) }),
  },
  users: {
    list: () => apiFetch<any[]>('/api/users'),
    shareable: () => apiFetch<any[]>('/api/users/shareable'),
    create: (body: any) => apiFetch<any>('/api/users', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: { name?: string; email?: string; password?: string; departmentId?: string; roleId?: string }) =>
      apiFetch<any>(`/api/users/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    delete: (id: string) => apiFetch<void>(`/api/users/${id}`, { method: 'DELETE' }),
  },
  system: {
    overview: (departmentId?: string) =>
      apiFetch<any>('/api/system/overview', {
        params: departmentId ? { departmentId } : undefined,
      }),
  },
  shares: {
    incoming: () => apiFetch<any[]>('/api/shares/incoming'),
    listForResource: (resourceType: 'file' | 'folder', resourceId: string) =>
      apiFetch<any[]>('/api/shares', { params: { resourceType, resourceId } }),
    create: (body: {
      resourceType: 'file' | 'folder';
      resourceId: string;
      sharedWithUserId: string;
      permissionLevel: 'view' | 'edit';
    }) => apiFetch<any>('/api/shares', { method: 'POST', body: JSON.stringify(body) }),
    setAccess: (body: {
      resourceType: 'file' | 'folder';
      resourceId: string;
      mode: 'department' | 'restricted';
      userIds?: string[];
      permissionLevel?: 'view' | 'edit';
    }) =>
      apiFetch<{ mode: string; visibility: string | null; shares: any[] }>('/api/shares/access', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    update: (id: string, body: { permissionLevel: 'view' | 'edit' }) =>
      apiFetch<any>(`/api/shares/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    delete: (id: string) => apiFetch<void>(`/api/shares/${id}`, { method: 'DELETE' }),
  },
};
